<script setup lang="ts">
import { ref } from 'vue'

const props = defineProps<{
  categoryId: string
  categoryLabel: string
}>()

const emit = defineEmits<{
  (e: 'confirm', payload: { id: string; label: string; mode: 'whitebox' | 'opacity' }): void
  (e: 'close'): void
}>()

const selectedMode = ref<'whitebox' | 'opacity'>('whitebox')
const saving = ref(false)

function confirm() {
  saving.value = true
  emit('confirm', {
    id: props.categoryId,
    label: props.categoryLabel,
    mode: selectedMode.value,
  })
}

function onBackdropClick(e: MouseEvent) {
  if (e.target === e.currentTarget) emit('close')
}
</script>

<template>
  <div class="hide-modal" @click="onBackdropClick">
    <div class="hide-modal__dialog" role="dialog" aria-label="Skjul kategori">
      <header class="hide-modal__header">
        <h3>Skjul kategori</h3>
        <button type="button" class="hide-modal__close" @click="emit('close')">×</button>
      </header>

      <div class="hide-modal__body">
        <p class="hide-modal__category-name">{{ categoryLabel }}</p>
        <p class="hide-modal__description">Velg hvordan kategorien skal skjules i SVG-bildet:</p>

        <div class="hide-modal__modes">
          <button
            type="button"
            class="hide-modal__mode-card"
            :class="{ 'hide-modal__mode-card--active': selectedMode === 'whitebox' }"
            @click="selectedMode = 'whitebox'"
          >
            <div class="hide-modal__mode-preview">
              <svg viewBox="0 0 80 40" class="hide-modal__preview-svg">
                <rect x="10" y="5" width="60" height="30" rx="4" fill="#d1d5db" />
                <text x="40" y="24" text-anchor="middle" font-size="10" fill="#374151">Abc</text>
                <rect x="7" y="2" width="66" height="36" rx="4" fill="white" opacity="0.92" />
              </svg>
            </div>
            <div class="hide-modal__mode-info">
              <strong>Hvit boks</strong>
              <span>Dekker elementet med en hvit boks</span>
            </div>
          </button>

          <button
            type="button"
            class="hide-modal__mode-card"
            :class="{ 'hide-modal__mode-card--active': selectedMode === 'opacity' }"
            @click="selectedMode = 'opacity'"
          >
            <div class="hide-modal__mode-preview">
              <svg viewBox="0 0 80 40" class="hide-modal__preview-svg">
                <rect x="10" y="5" width="60" height="30" rx="4" fill="#d1d5db" opacity="0.15" />
                <text x="40" y="24" text-anchor="middle" font-size="10" fill="#374151" opacity="0.15">Abc</text>
              </svg>
            </div>
            <div class="hide-modal__mode-info">
              <strong>Gjennomsiktighet</strong>
              <span>Gjør elementet usynlig</span>
            </div>
          </button>
        </div>
      </div>

      <footer class="hide-modal__footer">
        <button
          type="button"
          class="hide-modal__save"
          :disabled="saving"
          @click="confirm"
        >
          {{ saving ? 'Lagrer…' : 'Skjul' }}
        </button>
        <button
          type="button"
          class="hide-modal__cancel"
          :disabled="saving"
          @click="emit('close')"
        >
          Avbryt
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hide-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;
  z-index: 9999;

  &__dialog {
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    width: min(480px, 92vw);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1rem;
    border-bottom: 1px solid #e5e7eb;

    h3 {
      margin: 0;
      font-size: 1rem;
      color: #111827;
    }
  }

  &__close {
    background: transparent;
    border: none;
    font-size: 1.4rem;
    line-height: 1;
    cursor: pointer;
    color: #6b7280;

    &:hover { color: #111827; }
  }

  &__body {
    padding: 1rem;
  }

  &__category-name {
    margin: 0 0 0.25rem;
    font-weight: 600;
    font-size: 1.05rem;
    color: #111827;
    text-transform: capitalize;
  }

  &__description {
    margin: 0 0 1rem;
    font-size: 0.85rem;
    color: #6b7280;
  }

  &__modes {
    display: flex;
    gap: 0.75rem;
  }

  &__mode-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: white;
    border: 2px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;

    &:hover:not(&--active) {
      border-color: #9ca3af;
    }

    &--active {
      border-color: #002868;
      background: #f0f4ff;
    }
  }

  &__mode-preview {
    width: 100%;
    aspect-ratio: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f9fafb;
    border-radius: 4px;
  }

  &__preview-svg {
    width: 80%;
    height: auto;
  }

  &__mode-info {
    text-align: center;
    font-size: 0.8rem;
    color: #374151;

    strong {
      display: block;
      margin-bottom: 0.125rem;
      font-size: 0.85rem;
    }

    span {
      color: #6b7280;
      font-size: 0.75rem;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
  }

  &__save {
    padding: 6px 16px;
    background: #002868;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:hover:not(:disabled) { background: #001d4f; }
  }

  &__cancel {
    padding: 6px 14px;
    background: transparent;
    color: #4b5563;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.85rem;
    cursor: pointer;

    &:hover:not(:disabled) { background: #f3f4f6; }
  }
}
</style>
